import { Track } from "@/src/lib/tracking";
import type {
  DehydratedState,
  InfiniteData,
  QueryClient,
} from "@tanstack/react-query";
import { getOpenDataApi } from "./helper";
import { dehydrate, getQueryClient } from "@utils/withSSRQuery";
import { QUERY_KEYS } from "@config/constant";
import {
  ThemeItem,
  ThemeSymbol,
  ThemeItemPaginationResponse,
  OpenDataTypeEnum,
} from "@features/api/openData";
import { ThemeWithMarketData } from "@componentsv1/themes/type";
import { Locale } from "@utils/i18n.locale";
import { I18nUtil } from "@utils/i18n";
import {
  StockMarketType,
  ThemesSortFieldEnum,
  ThemesScopeEnum,
} from "@const/market";

// 主题分页数据类型
export interface ThemesPagination {
  rows: ThemeItem[];
  total: number;
  offset: number;
  limit: number;
}

// 获取主题排名数据的参数
export interface GetRankingThemesParams {
  offset: number;
  limit: number;
  type: OpenDataTypeEnum;
  "sort.field": ThemesSortFieldEnum;
  "sort.order": "DESC" | "ASC";
  "enrich.with_market.enable": boolean;
  "enrich.with_symbol.enable": boolean;
  "enrich.with_symbol.limit": number;
  "enrich.enable": boolean;
  market?: string; // 股票市场（US/HK），仅当 type 为 STOCK 时有效，后端接收字符串
  scope?: ThemesScopeEnum; // 主题类型（hotSearch, heatmap, recommend），后端实际使用 PascalCase：Recommend / HeatMap / HotSearch
  language: string; // 语言参数（必传）
}

// 构建查询参数的配置接口（用于创建 GetRankingThemesParams）
export interface ThemesQueryConfig {
  offset: number;
  limit: number;
  type: OpenDataTypeEnum;
  sortField?: ThemesSortFieldEnum;
  sortOrder: "DESC" | "ASC";
  enrichWithMarket: boolean;
  enrichWithSymbol: boolean;
  enrichWithSymbolLimit: number;
  enrichEnable: boolean;
  stockType?: StockMarketType; // 股票市场类型（US/HK），仅当 type 为 STOCK 时使用
  scope?: ThemesScopeEnum; // 主题类型（hotSearch, heatmap, recommend）
  /**
   * @description 前端 locale（如 en、zh），用于统一转换为后端 language
   * @required false
   */
  locale?: Locale;
}

/**
 * 构建主题查询参数（统一参数构建逻辑）
 */
export function buildThemesQueryParams(
  config: ThemesQueryConfig,
): GetRankingThemesParams {
  const language = I18nUtil.getBackgroundLanguageKey(config.locale as Locale);

  const params: GetRankingThemesParams = {
    offset: config.offset,
    limit: config.limit,
    type: config.type,
    scope: config.scope,
    "sort.field": config.sortField ?? ThemesSortFieldEnum.MANUAL,
    "sort.order": config.sortOrder,
    "enrich.with_market.enable": config.enrichWithMarket,
    "enrich.with_symbol.enable": config.enrichWithSymbol,
    "enrich.with_symbol.limit": config.enrichWithSymbolLimit,
    "enrich.enable": config.enrichEnable,
    language, // 统一转换后的语言参数
  };

  // 仅当 type 为 STOCK 且提供了 stockType 时，添加 market 参数
  if (config.type === OpenDataTypeEnum.STOCK) {
    params.market = config.stockType || StockMarketType.US;
  }

  return params;
}

/**
 * 构建 React Query 的 queryKey（包含所有影响查询结果的参数）
 */
export function buildThemesQueryKey(config: Omit<ThemesQueryConfig, "offset">) {
  const language = I18nUtil.getBackgroundLanguageKey(config.locale as Locale);

  return [
    QUERY_KEYS.RANKING_THEMES,
    config.type,
    config.sortField,
    config.sortOrder,
    config.limit,
    config.enrichWithMarket,
    config.enrichWithSymbol,
    config.enrichWithSymbolLimit,
    config.enrichEnable,
    config.stockType, // 包含 stockType 以区分港股和美股
    config.scope, // 包含 scope 以区分不同的主题类型
    language, // 包含 language 以区分不同语言
  ];
}

// ============================================================================
// Recommend / 24h 排名公共参数构建（保证多处调用的一致性）
// ============================================================================

/**
 * Recommend / 24h 排名公共配置
 */
export interface RecommendThemesBaseConfig {
  type: OpenDataTypeEnum;
  locale: Locale;
  stockType?: StockMarketType;
}

/**
 * 构建 Recommend 排名的基础参数（不包含 limit / enabledDehydrate）
 * - 统一使用 MANUAL 排序
 * - 统一开启 enrichWithMarket / enrichWithSymbol / enrichEnable
 * - 统一使用 Recommend scope
 */
export const buildRecommendThemesBaseParams = (
  config: RecommendThemesBaseConfig,
) => {
  return {
    type: config.type,
    sortField: ThemesSortFieldEnum.MANUAL,
    sortOrder: "ASC" as const,
    enrichWithMarket: true,
    enrichWithSymbol: true,
    enrichWithSymbolLimit: 5,
    enrichEnable: true,
    stockType: config.stockType,
    scope: ThemesScopeEnum.Recommend,
    locale: config.locale,
  };
};

/**
 * 构建 24h 涨跌幅排名的基础参数（不包含 limit / enabledDehydrate）
 * - 统一使用 PRICE_CHANGE_24H 排序
 * - 统一开启 enrichWithMarket / enrichWithSymbol / enrichEnable
 */
export const build24hThemesBaseParams = (config: RecommendThemesBaseConfig) => {
  return {
    type: config.type,
    sortField: ThemesSortFieldEnum.PRICE_CHANGE_24H,
    sortOrder: "DESC" as const,
    enrichWithMarket: true,
    enrichWithSymbol: true,
    enrichWithSymbolLimit: 20,
    enrichEnable: true,
    stockType: config.stockType,
    locale: config.locale,
  };
};

// SSR 函数参数
interface GetRankingThemesSSRParams {
  queryClient?: QueryClient;
  enabledDehydrate?: boolean;
  type: OpenDataTypeEnum;
  sortField?: ThemesSortFieldEnum;
  limit?: number; // 可选的每页数量，默认 20
  sortOrder?: "DESC" | "ASC"; // 排序顺序，默认 DESC
  enrichWithMarket?: boolean; // 是否包含市场数据，默认 false
  enrichWithSymbol?: boolean; // 是否包含标的数据，默认 false
  enrichWithSymbolLimit?: number; // 标的数量限制，默认 5
  enrichEnable?: boolean; // 是否启用丰富化，默认 false
  stockType?: StockMarketType; // 股票市场类型（US/HK），仅当 type 为 STOCK 时使用
  scope?: ThemesScopeEnum; // 主题类型（hotSearch, heatmap, recommend）
  /**
   * @description 前端 locale（如 en、zh），用于统一转换 language
   * @required true
   */
  locale: Locale;
}

interface GetRankingThemesSSRResultWithDehydrate {
  rankingThemes: InfiniteData<ThemesPagination> | undefined;
  dehydratedState: DehydratedState;
  queryClient: QueryClient;
}

interface GetRankingThemesSSRResultWithoutDehydrate {
  rankingThemes: InfiniteData<ThemesPagination> | undefined;
  queryClient: QueryClient;
}

// 函数重载：enabledDehydrate 为 true 时，返回带 dehydratedState，便于调用方拿到注水状态
export async function getRankingThemesSSR(
  params: GetRankingThemesSSRParams & { enabledDehydrate: true },
): Promise<GetRankingThemesSSRResultWithDehydrate>;

// 函数重载：enabledDehydrate 为 false 或未传时，不返回 dehydratedState（首页等仅用数据、不注水）
export async function getRankingThemesSSR(
  params: GetRankingThemesSSRParams & { enabledDehydrate?: false },
): Promise<GetRankingThemesSSRResultWithoutDehydrate>;

// 函数实现
export async function getRankingThemesSSR(
  params: GetRankingThemesSSRParams,
): Promise<
  | GetRankingThemesSSRResultWithDehydrate
  | GetRankingThemesSSRResultWithoutDehydrate
> {
  const {
    enabledDehydrate,
    type,
    sortField,
    limit = 20,
    sortOrder = "DESC",
    enrichWithMarket = false,
    enrichWithSymbol = false,
    enrichWithSymbolLimit = 5,
    enrichEnable = false,
    scope,
    locale,
  } = params;
  const queryClient = params.queryClient || getQueryClient();

  // 构建查询配置
  const queryConfig: ThemesQueryConfig = {
    offset: 0,
    limit,
    type,
    sortField,
    sortOrder,
    enrichWithMarket,
    enrichWithSymbol,
    enrichWithSymbolLimit,
    enrichEnable,
    stockType: params.stockType,
    scope: scope as unknown as ThemesScopeEnum,
    locale,
  };

  // 构建 QUERY_KEY（包含所有影响查询结果的参数）
  const QUERY_KEY = buildThemesQueryKey(queryConfig);

  // 构建初始查询参数
  const initialPageParam = buildThemesQueryParams(queryConfig);

  const openDataApi = getOpenDataApi();

  await queryClient.prefetchInfiniteQuery({
    queryKey: QUERY_KEY,
    queryFn: async ({ pageParam }) => {
      const response = await openDataApi?.themes.getRankingThemes(pageParam);

      const paginationData = (response?.data as ThemeItemPaginationResponse)
        ?.data;

      if (!paginationData || !Array.isArray(paginationData.rows)) {
        Track.log("warn", "getRankingThemesSSR", `返回数据为空或格式错误`);
        return {
          rows: [],
          total: 0,
          offset: pageParam.offset,
          limit: pageParam.limit,
        };
      }

      return {
        rows: paginationData.rows,
        total: paginationData.total || 0,
        offset: pageParam.offset,
        limit: pageParam.limit,
      };
    },
    initialPageParam,
  });

  const rankingThemes =
    queryClient.getQueryData<InfiniteData<ThemesPagination>>(QUERY_KEY);

  if (enabledDehydrate) {
    const dehydratedState = dehydrate(queryClient);
    return {
      rankingThemes,
      dehydratedState,
      queryClient,
    };
  }

  return { rankingThemes, queryClient };
}

// ============================================================================
// 数据转换函数
// ============================================================================

/**
 * 从主题 images 中提取用于展示的图片 URL
 * 约定：images 为对象数组，从中找到 type === "large" 的 url
 * 若找不到则返回空字符串
 */
export const getThemeImage = (
  images?: { type?: string; url?: string }[] | null,
): string => {
  if (!Array.isArray(images) || images.length === 0) {
    return "";
  }

  const largeImage = images.find((img) => img?.type === "large");
  return largeImage?.url ?? "";
};

/**
 * 将接口主题数据转换为带市场数据的主题格式
 * @param themeItem 新接口返回的主题数据
 * @param locale 语言代码
 * @param mode 市场模式
 * @returns 转换后的主题数据
 */
const transformThemeItem = (
  themeItem: ThemeItem,
): ThemeWithMarketData | null => {
  if (!themeItem.theme) {
    return null;
  }

  // 转换symbols数据
  const symbols =
    themeItem.symbols
      ?.map((symbol: ThemeSymbol) => symbol.logo)
      .filter((logo): logo is string => Boolean(logo)) || [];

  // 提取性能数据
  const marketData = themeItem.market_data;
  const performance = {
    percentage: marketData?.price_change_percentage?.percentage,
    beta: marketData?.beta,
  };

  // 提取主题图片（可复用）
  const image = getThemeImage(
    themeItem.images as unknown as { type?: string; url?: string }[] | null,
  );

  return {
    // ...themeInfo,
    ...themeItem,
    image,
    symbols,
    performance,
  };
};

/**
 * 批量转换主题数据
 * @param themeItems 新接口返回的主题数据数组
 * @param locale 语言代码
 * @param mode 市场模式
 * @returns 转换后的主题数据数组
 */
export const transformThemes = (
  themeItems: ThemeItem[],
): ThemeWithMarketData[] => {
  return themeItems
    .map((item) => transformThemeItem(item))
    .filter((item): item is ThemeWithMarketData => item !== null);
};
